import { FunctionComponent, JSX, useState } from 'react';
import * as vp from 'vopay';
import * as Yup from 'yup';
import { Formik } from 'formik';
import type { CreateTicketCommentHandlers } from '../types';
import { useBuildTicketCommentCreationRows } from './buildTicketCommentRows.helper';
import styles from './TicketComments.module.scss';
import { useTranslation } from 'react-i18next';

const { Box, Button, TextArea, ElementContainer, Tooltip, Icon, MultiInputFile } = vp;

export type SubmitTicketCommentRequstAndFiles = vp.SubmitTicketCommentRequst & {
  files: File[];
};
type TicketCommentCreationProps = {
  handlers: CreateTicketCommentHandlers;
  defaultValues: SubmitTicketCommentRequstAndFiles;
};

type FC = FunctionComponent<TicketCommentCreationProps>;

const TicketCommentsForm: FC = (props: TicketCommentCreationProps) => {
  const { handlers, defaultValues } = props;

  const ticketRow = useBuildTicketCommentCreationRows();
  const [showingReply, setShowingReply] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'TicketsList.Details.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={(values, { resetForm }) => {
        handlers.onCreateComment(values);
        resetForm();
        setShowingReply(false);
      }}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, resetForm }) => {
        const ticketsForm = ticketRow.map(({ label, id, type }) => getField(id, label, type));

        return (
          <form onSubmit={handleSubmit}>
            {!showingReply && (
              <Box jc="fe">
                <Button
                  id="CreateTicket-reply"
                  type="button"
                  label={tGlobal('Buttons.reply')}
                  height="36px"
                  onClick={() => {
                    setShowingReply(true);
                  }}
                />
              </Box>
            )}
            {showingReply && (
              <>
                {...ticketsForm}
                <Box mt="md" jc="fe" gap="sm">
                  <Button
                    id="CreateTicket-cancel"
                    color="secondary"
                    height="36px"
                    type="button"
                    onClick={() => {
                      setShowingReply(false);
                      resetForm();
                    }}
                    label={tGlobal('Buttons.cancel')}
                  />
                  <Button id="CreateTicket-submit" height="36px" type="submit" label={tGlobal('Buttons.send')} />
                </Box>
              </>
            )}
          </form>
        );
        function getField(id: vp.CreateTicketCommentId, label: string, type: 'textarea' | 'fileInput') {
          return (
            <>
              {type === 'textarea' && (
                <ElementContainer
                  label={
                    <Box ai="fs">
                      <span>{label}</span> {getRequiredIcon(true)}
                    </Box>
                  }
                  isBold={false}
                  padding="0px 0 8px"
                  input={
                    <>
                      <TextArea
                        variant="lined"
                        key={id}
                        name={id}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        maxLength={5000}
                        borderColor={errors[id] && touched[id] ? '#D22D2D' : '#C4C4C4'}
                      />
                      <Box mt="sm" jc="sb">
                        <span
                          className={[styles.commentLength, errors[id] && touched[id] ? styles.error : ''].join(' ')}
                        >
                          {values[id].length}/5000 {errors[id] && touched[id] ? errors[id]?.toString() : ''}
                        </span>

                        <Box gap="sm" jc="fe">
                          <span>{t('bodyInformation')}</span>
                          <Tooltip
                            type="dialogue"
                            icon="question"
                            iconColor="primary-light"
                            information=""
                            onClick={() => {
                              handlers.onShowTicketTips();
                            }}
                          />
                        </Box>
                      </Box>
                    </>
                  }
                />
              )}
              {type === 'fileInput' && (
                <Box pt="md" mt="sm">
                  <MultiInputFile
                    id={id}
                    name={id}
                    onChange={(files: File[] | null, name: string) => {
                      setFieldValue(name, files);
                    }}
                    allowedExtensions={['png', 'jpg', 'pdf']}
                    placeholder={t('inputFilePlaceholder')}
                  />
                </Box>
              )}
            </>
          );
        }
      }}
    </Formik>
  );
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
  function createdValidationSchema() {
    return Yup.object().shape({
      TicketComment: Yup.string()
        .test(
          'TicketComment',
          t('ticketCommentValidation'),
          (value: string | undefined) => value !== undefined && value !== null && value.trim() !== '',
        )
        .required(),
    });
  }
};

export default TicketCommentsForm;
